import { useEffect, useState } from 'react';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { canAdministerSpoke, Capability } from '@internal/datasphere-personas';
import { usePersonaSession } from '@internal/datasphere-personas/react';

export const NOT_SPOKE_ADMIN_HINT =
  'Greyed out spokes: you are not in their spoke admins group.';

export interface DatabricksWorkspace {
  name: string;
  location?: string;
  workspaceUrl?: string;
}

export interface DatabricksWorkspaceSelection {
  spoke: string;
  environments: string[];
  workspaces: string[];
  workspaceDetails: { name: string; environment: string }[];
}

export const parseWorkspaceName = (
  name: string,
): { spoke: string; environment: string } | undefined => {
  const parts = name.split('-').filter(Boolean);
  const adbIndex = parts.findIndex(part => part.toLowerCase() === 'adb');
  const spokeStart = adbIndex + 1;
  const environmentIndex = parts.length - 2;

  if (
    adbIndex < 0 ||
    spokeStart >= environmentIndex ||
    environmentIndex <= spokeStart
  ) {
    return undefined;
  }

  const spokeParts = parts
    .slice(spokeStart, environmentIndex)
    .filter(part => part.toLowerCase() !== 'gdl');
  const environment = parts[environmentIndex];
  return spokeParts.length > 0 && environment
    ? { spoke: spokeParts.join('-'), environment }
    : undefined;
};

const getSelectedWorkspaceDetails = (
  options: DatabricksWorkspace[],
  spoke: string,
  environments: string[],
) =>
  options
    .map(workspace => {
      const parsed = parseWorkspaceName(workspace.name);
      return parsed
        ? { name: workspace.name, environment: parsed.environment }
        : undefined;
    })
    .filter((workspace): workspace is { name: string; environment: string } =>
      Boolean(
        workspace &&
          environments.includes(workspace.environment) &&
          parseWorkspaceName(workspace.name)?.spoke === spoke,
      ),
    );

export const DatabricksWorkspaceField = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
}: FieldExtensionComponentProps<DatabricksWorkspaceSelection>) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const persona = usePersonaSession();
  // Presentation only: howden:spoke-admin:check refuses the request in the
  // backend. An Admin is not scoped, so sees every spoke as selectable.
  const isAdmin = persona.can(Capability.AdminAccess);
  const canPickSpoke = (spoke: string) =>
    canAdministerSpoke(persona.personas, persona.spokeAdminOf, spoke);

  const [options, setOptions] = useState<DatabricksWorkspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | undefined>();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const azureBase = await discoveryApi.getBaseUrl('azure');
        const response = await fetchApi.fetch(
          `${azureBase}/databricks/workspaces`,
        );
        if (!response.ok) {
          throw new Error(`${response.status}`);
        }
        const workspaces: DatabricksWorkspace[] = await response.json();
        if (!cancelled) {
          setOptions(workspaces);
        }
      } catch {
        if (!cancelled) {
          setLoadError('Failed to load Databricks workspaces');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi]);

  const spokeOptions = Array.from(
    new Set(
      options
        .map(workspace => parseWorkspaceName(workspace.name)?.spoke)
        .filter((spoke): spoke is string => Boolean(spoke)),
    ),
  ).sort();
  const selectedSpoke = formData?.spoke ?? null;
  const environmentOptions = Array.from(
    new Set(
      options
        .filter(
          workspace =>
            parseWorkspaceName(workspace.name)?.spoke === selectedSpoke,
        )
        .map(workspace => parseWorkspaceName(workspace.name)?.environment)
        .filter((environment): environment is string => Boolean(environment)),
    ),
  ).sort();
  const selectedEnvironments = formData?.environments ?? [];
  const selectedWorkspaces = options
    .filter(workspace => {
      const parsed = parseWorkspaceName(workspace.name);
      return (
        parsed?.spoke === selectedSpoke &&
        selectedEnvironments.includes(parsed.environment)
      );
    })
    .map(workspace => workspace.name);
  const hasError = !!rawErrors?.length || !!loadError;

  return (
    <>
      <Autocomplete
        fullWidth
        options={spokeOptions}
        getOptionDisabled={spoke => !canPickSpoke(spoke)}
        loading={loading || persona.loading}
        value={selectedSpoke}
        onChange={(_, spoke: string | null) => {
          onChange(
            spoke
              ? {
                  spoke,
                  environments: [],
                  workspaces: [],
                  workspaceDetails: [],
                }
              : undefined,
          );
        }}
        noOptionsText={loading ? 'Loading workspaces...' : 'No spokes found'}
        renderInput={params => (
          <TextField
            {...params}
            label={schema?.title ?? 'Databricks Spoke'}
            required={required}
            error={hasError}
            helperText={
              hasError
                ? loadError ?? rawErrors?.join(', ')
                : [
                    schema?.description,
                    isAdmin ? undefined : NOT_SPOKE_ADMIN_HINT,
                  ]
                    .filter(Boolean)
                    .join(' ')
            }
            margin="normal"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {selectedSpoke && (
        <FormControl component="fieldset" margin="normal" required={required}>
          <FormLabel component="legend">Environment</FormLabel>
          {environmentOptions.map(environment => (
            <FormControlLabel
              key={environment}
              control={
                <Checkbox
                  checked={selectedEnvironments.includes(environment)}
                  onChange={event => {
                    const environments = event.target.checked
                      ? [...selectedEnvironments, environment]
                      : selectedEnvironments.filter(
                          value => value !== environment,
                        );
                    const workspaces = Array.from(
                      new Set(
                        options
                          .filter(workspace => {
                            const parsed = parseWorkspaceName(workspace.name);
                            return (
                              parsed?.spoke === selectedSpoke &&
                              environments.includes(parsed.environment)
                            );
                          })
                          .map(workspace => workspace.name),
                      ),
                    );
                    onChange(
                      environments.length > 0
                        ? {
                            spoke: selectedSpoke,
                            environments,
                            workspaces,
                            workspaceDetails: getSelectedWorkspaceDetails(
                              options,
                              selectedSpoke,
                              environments,
                            ),
                          }
                        : undefined,
                    );
                  }}
                />
              }
              label={environment}
            />
          ))}
        </FormControl>
      )}
      {selectedSpoke && selectedWorkspaces.length === 0 && (
        <TextField
          fullWidth
          error={hasError}
          helperText="Select at least one environment."
          margin="normal"
          value=""
          InputProps={{ readOnly: true }}
        />
      )}
    </>
  );
};
